/**
 * Sign-in routing by email domain (VSNP-1629, doc 28d) — the display helpers
 * the admin views share. The API is the authority on every route (the access
 * block, the bundle, the preview, a user's signInRoute); these helpers only
 * label what it returns.
 */
import type {
  IdentityProvider,
  IdentityProviderDomain,
  IdentityProviderRouting,
  IdentityProviderType,
} from '../services/access.service';
import type { ActivityRecord } from '../services/activity.service';

export const PROVIDER_TYPE_LABEL: Record<IdentityProviderType, string> = {
  azuread: 'Azure AD (Entra ID)',
  oidc: 'Generic OIDC',
};

/** "Microsoft" / "your organisation's sign-in", as the user-facing lines say it. */
export function providerTypeShort(type: IdentityProviderType | null | undefined): string {
  return type === 'azuread' ? 'Azure AD / Entra ID' : type === 'oidc' ? 'OIDC' : 'an identity provider';
}

/** A provider's routing (an older API sends none: that is `all`). */
export function providerRouting(p: Pick<IdentityProvider, 'routing'>): IdentityProviderRouting {
  return p.routing === 'domains' ? 'domains' : 'all';
}

export function providerDomains(p: Pick<IdentityProvider, 'domains'>): IdentityProviderDomain[] {
  return p.domains ?? [];
}

export function verifiedDomains(p: Pick<IdentityProvider, 'domains'>): IdentityProviderDomain[] {
  return providerDomains(p).filter((d) => d.status === 'verified');
}

/** The domain as an admin reads it: the U-label for an IDN, else the A-label. */
export function domainLabel(d: Pick<IdentityProviderDomain, 'domain' | 'domainUnicode'>): string {
  return d.domainUnicode || d.domain;
}

export type DomainBadgeKind = 'pending' | 'verified' | 'attested' | 'recheck_failed';

/**
 * The status badge (28d §10.1): pending; verified (DNS); attested (a
 * SuperAdmin); or verified by DNS whose last re-check failed (it stays
 * verified and keeps routing — an admin removes it explicitly).
 */
export function domainBadge(d: Pick<IdentityProviderDomain, 'status' | 'verificationMethod' | 'lastCheckError'>): {
  kind: DomainBadgeKind;
  label: string;
  className: string;
} {
  if (d.status !== 'verified') {
    return { kind: 'pending', label: 'Pending', className: 'bg-warning-100 text-warning-700 border-warning-200' };
  }
  if (d.verificationMethod === 'operator') {
    return { kind: 'attested', label: 'Attested', className: 'bg-purple-100 text-purple-700 border-purple-200' };
  }
  if (d.lastCheckError) {
    return { kind: 'recheck_failed', label: 'Re-check failed', className: 'bg-danger-100 text-danger-700 border-danger-200' };
  }
  return { kind: 'verified', label: 'Verified', className: 'bg-success-100 text-success-700 border-success-200' };
}

/**
 * Before sign-in the new email-first apps do not know the tenant, so they find
 * a provider only through a verified domain — in both routing modes (in `all`
 * mode the domains are for discovery only; after registration everyone still
 * uses the provider). An enabled `all` provider with no verified domain is
 * therefore invisible to them, and its users get a password prompt.
 */
export const NO_VERIFIED_DOMAIN_NOTICE =
  'Users of the new SecureLink apps find this provider by their email domain. Add and verify your domains, or they will be asked for a password.';

export function needsVerifiedDomainNotice(p: Pick<IdentityProvider, 'enabled' | 'routing' | 'domains'>): boolean {
  return p.enabled && providerRouting(p) === 'all' && verifiedDomains(p).length === 0;
}

export type TenantRoutingMode = 'local' | 'all' | 'domains';

/** 28d §2.1: domain mode when any enabled provider routes by domain; all mode with an enabled provider; else local. */
export function tenantRoutingMode(providers: readonly IdentityProvider[]): TenantRoutingMode {
  const enabled = providers.filter((p) => p.enabled);
  if (enabled.some((p) => providerRouting(p) === 'domains')) return 'domains';
  return enabled.length > 0 ? 'all' : 'local';
}

/**
 * Whether the tenant's enabled providers would mix `all` and `domains` after
 * `providerId` changes routing (the API refuses it: 400 mixed_routing, 28d §2.2).
 */
export function routingWouldMix(
  providers: readonly IdentityProvider[],
  providerId: string,
  change: { routing?: IdentityProviderRouting; enabled?: boolean },
): boolean {
  const after = providers.map((p) =>
    p.id === providerId
      ? { enabled: change.enabled ?? p.enabled, routing: change.routing ?? providerRouting(p) }
      : { enabled: p.enabled, routing: providerRouting(p) },
  );
  return new Set(after.filter((p) => p.enabled).map((p) => p.routing)).size > 1;
}

// ---------------------------------------------------------------------------
// Activity (28d §10.3)
// ---------------------------------------------------------------------------

const ACTIVITY_LABEL: Record<string, string> = {
  'access_identity_provider.create': 'Provider created',
  'access_identity_provider.update': 'Provider updated',
  'access_identity_provider.delete': 'Provider deleted',
  'access_identity_provider.domain_add': 'Domain added',
  'access_identity_provider.domain_verify': 'Domain verified (DNS)',
  'access_identity_provider.domain_verify_failed': 'Domain check failed',
  'access_identity_provider.domain_attest': 'Domain attested',
  'access_identity_provider.domain_remove': 'Domain removed',
  'access_identity_provider.routing_change': 'Routing changed',
  'access_sign_in.idp_domain_mismatch': 'Sign-in refused: domain mismatch',
  'access_sign_in.idp_email_unverified': 'Sign-in refused: unverified email',
};

const ROUTING_WORDS: Record<string, string> = { all: 'everyone in the tenant', domains: 'only its domains' };

function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function previewWords(details: Record<string, unknown>): string | null {
  const users = details.users as Record<string, unknown> | undefined;
  const disc = details.discovery as Record<string, unknown> | undefined;
  if ((!users || typeof users !== 'object') && (!disc || typeof disc !== 'object')) return null;
  const out: string[] = [];
  if (users && typeof users === 'object') {
    const parts: string[] = [];
    const toProvider = num(users.toProvider);
    const toPassword = num(users.toPassword);
    const changed = num(users.providerChanged);
    if (toProvider) parts.push(`${toProvider} to the provider`);
    if (toPassword) parts.push(`${toPassword} to the password`);
    if (changed) parts.push(`${changed} to another provider`);
    const stranded = num(details.strandedBrokeredOnly);
    if (stranded) parts.push(`${stranded} without a password`);
    if (parts.length) out.push(`users: ${parts.join(', ')}`);
  }
  // Before sign-in, what the new apps get from the email (VSNP-1707): counts only.
  if (disc && typeof disc === 'object') {
    const parts: string[] = [];
    const toPassword = num(disc.toPassword);
    const toProvider = num(disc.toProvider);
    const changed = num(disc.providerChanged);
    const stranded = num(disc.strandedBrokeredOnly);
    if (toPassword) parts.push(`${toPassword} asked for a password`);
    if (toProvider) parts.push(`${toProvider} sent to the provider`);
    if (changed) parts.push(`${changed} to another provider`);
    if (stranded) parts.push(`${stranded} without a password`);
    if (parts.length) out.push(`new apps: ${parts.join(', ')}`);
  }
  return out.length ? out.join('; ') : 'no user changed sign-in method';
}

/** True for the actions this module labels (the identity-provider and sign-in-routing rows). */
export function isSignInRoutingActivity(action: string | null | undefined): boolean {
  return !!action && (action.startsWith('access_identity_provider.') || action.startsWith('access_sign_in.'));
}

/**
 * A readable label and one-line summary for an identity-provider activity row
 * (28d §10.3), or null for any other action. Only what the API recorded is
 * shown: domains (never a local part), the alias, the reason, the counts.
 */
export function describeSignInRoutingActivity(
  r: Pick<ActivityRecord, 'action' | 'details' | 'resource_name'>,
): { label: string; summary: string } | null {
  if (!isSignInRoutingActivity(r.action)) return null;
  const d = (r.details ?? {}) as Record<string, unknown>;
  const label = ACTIVITY_LABEL[r.action] ?? r.action;
  const alias = str(d.alias) ?? r.resource_name ?? null;
  const domain = str(d.domain);
  const on = alias ? ` on ${alias}` : '';
  let summary: string;
  switch (r.action) {
    case 'access_identity_provider.domain_add':
      summary = `${domain ?? 'a domain'}${on} — pending until verified`;
      break;
    case 'access_identity_provider.domain_verify':
      summary = `${domain ?? 'a domain'}${on} — DNS TXT record found${d.newlyVerified === false ? ' (re-check)' : ''}`;
      break;
    case 'access_identity_provider.domain_verify_failed':
      summary = `${domain ?? 'a domain'}${on}${d.recheck ? ' — 24 h re-check' : ''}: ${str(d.error) ?? 'the DNS check failed'}`;
      break;
    case 'access_identity_provider.domain_attest':
      summary = `${domain ?? 'a domain'}${on} — reason: ${str(d.reason) ?? '(none)'}`;
      break;
    case 'access_identity_provider.domain_remove': {
      const p = previewWords((d.preview ?? {}) as Record<string, unknown>);
      summary = `${domain ?? 'a domain'}${on}${d.reason ? ` — reason: ${String(d.reason)}` : ''}${p ? ` — ${p}` : ''}${d.confirm ? ' (confirmed)' : ''}`;
      break;
    }
    case 'access_identity_provider.routing_change': {
      const from = ROUTING_WORDS[String(d.from)] ?? String(d.from ?? '?');
      const to = ROUTING_WORDS[String(d.to)] ?? String(d.to ?? '?');
      const p = previewWords(d);
      summary = `${alias ?? 'provider'}: ${from} → ${to}${p ? ` — ${p}` : ''}${d.confirm ? ' (confirmed)' : ''}`;
      break;
    }
    case 'access_sign_in.idp_domain_mismatch':
    case 'access_sign_in.idp_email_unverified':
      summary = `${alias ?? 'provider'}${domain ? `, email domain ${domain}` : ''} — ${str(d.outcome) ?? 'refused'}`;
      break;
    default:
      summary = alias ?? '';
  }
  return { label, summary };
}

/** Badge colours for the routing actions (the Audit Log's action badge). */
export function signInRoutingActivityTone(action: string): string {
  if (action.endsWith('.domain_add')) return 'bg-green-100 text-green-800';
  if (action.endsWith('.domain_remove') || action.endsWith('.delete') || action.startsWith('access_sign_in.')) return 'bg-red-100 text-red-800';
  if (action.endsWith('.domain_verify_failed')) return 'bg-orange-100 text-orange-800';
  if (action.endsWith('.domain_attest')) return 'bg-purple-100 text-purple-800';
  if (action.endsWith('.routing_change')) return 'bg-indigo-100 text-indigo-800';
  return 'bg-blue-100 text-blue-800';
}
